import { useState } from "react";
import { Stamp, X, CheckCircle2, MessageCircle, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Format an IDR amount the Indonesian way: dot thousands, no decimals.
 */
function formatIDR(n) {
  return `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
}

/**
 * Inline "Apply e-Meterai" button for a row.
 * - Renders nothing if the doc doesn't need / can't get a stamp.
 * - Shows "Stamped" pill once applied.
 * - Opens a confirmation modal before consuming the stamp quota.
 *
 * Props:
 *   kind:           'memo' | 'sale'
 *   doc:            the document object (must have id + value + signed + pending_emeterai + emeterai_applied)
 *   onApplied:      callback after the stamp lands (parent reloads list)
 *   testidPrefix:   e.g. memo_number or invoice_number — used to make stable testids
 */
export function EmeteraiApplyButton({ kind, doc, onApplied, testidPrefix }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (doc.emeterai_applied) {
    return (
      <span
        className="min-h-[44px] sm:min-h-0 text-[0.6rem] uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#7BA585] border border-[#7BA585]/30"
        title={`Stamped · ${doc.emeterai_serial}`}
        data-testid={`emeterai-stamped-${testidPrefix}`}
      >
        <Stamp className="w-3 h-3" /> Stamped
      </span>
    );
  }
  if (!doc.signed || !doc.pending_emeterai) return null;
  return (
    <>
      <button
        onClick={() => setConfirmOpen(true)}
        data-testid={`apply-emeterai-${testidPrefix}`}
        className="min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#C99356] hover:text-[#E5B481] border border-[#C99356]/30 bg-[#C99356]/[0.04]"
        title="Apply Indonesian e-Meterai stamp"
      >
        <Stamp className="w-3 h-3" /> Apply e-Meterai
      </button>
      {confirmOpen && (
        <EmeteraiConfirmModal
          kind={kind}
          doc={doc}
          onClose={() => setConfirmOpen(false)}
          onApplied={(stamp) => {
            setConfirmOpen(false);
            onApplied?.(stamp);
          }}
        />
      )}
    </>
  );
}

function EmeteraiConfirmModal({ kind, doc, onClose, onApplied }) {
  const [submitting, setSubmitting] = useState(false);
  const value = kind === "memo" ? doc.total_value : doc.total;
  const docNumber = kind === "memo" ? doc.memo_number : doc.invoice_number;

  const submit = async () => {
    setSubmitting(true);
    try {
      const path =
        kind === "memo"
          ? `/memos/${doc.id}/apply-emeterai`
          : `/accounting/sales/${doc.id}/apply-emeterai`;
      const { data } = await api.post(path);
      toast.success(`e-Meterai applied · ${data.emeterai_serial}`);
      onApplied(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply stamp");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="emeterai-confirm-modal">
      <div className="relative bg-[#0F0F0F] border border-[#C99356]/40 w-full max-w-lg fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline text-[#C99356]">Apply e-Meterai</div>
            <h3 className="serif text-2xl mt-1">Confirm stamp</h3>
          </div>
          <button onClick={onClose} data-testid="emeterai-confirm-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          <div className="flex items-start gap-3 border border-[#C99356]/30 bg-[#C99356]/[0.04] p-4">
            <AlertTriangle className="w-4 h-4 text-[#C99356] mt-0.5 shrink-0" />
            <div className="text-sm text-[#EAEAEA] leading-relaxed">
              You are about to consume <strong className="text-[#C99356]">1 e-Meterai quota (Rp 10.000)</strong>.
              This applies a legally-binding Indonesian digital stamp duty to{" "}
              <strong>{docNumber}</strong>. The document is already signed and locked — after stamping,
              the stamped PDF replaces the unstamped one and cannot be reversed.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="overline text-[0.55rem]">Document</div>
              <div className="mono mt-1">{docNumber}</div>
            </div>
            <div>
              <div className="overline text-[0.55rem]">Document value</div>
              <div className="serif text-lg text-[#D4AF37] mt-0.5">{formatIDR(value)}</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-outline-gold px-5 py-2.5" data-testid="emeterai-cancel">Cancel</button>
            <button
              onClick={submit}
              disabled={submitting}
              className="btn-gold px-5 py-2.5"
              data-testid="emeterai-confirm-apply"
            >
              {submitting ? "Applying…" : "Confirm & Apply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * "Send to customer" panel — shown after the document is signed (and ideally
 * stamped). Three actions:
 *   - View Signed PDF
 *   - WhatsApp CTA (opens wa.me with prefilled link)
 *   - Copy Public URL
 */
export function SendPanel({ kind, doc, shareToken, onClose }) {
  const customerPhone = doc.customer_phone || "";
  const customerName = doc.customer_name || "customer";
  const docNumber = kind === "memo" ? doc.memo_number : doc.invoice_number;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/sign/${kind}/${shareToken}/pdf`;
  const signUrl = `${window.location.origin}/sign/${kind}/${shareToken}`;
  const waNum = (customerPhone || "").replace(/[^\d]/g, "");
  const stampedLabel = doc.emeterai_applied ? "signed + stamped" : "signed";
  const waMsg = encodeURIComponent(
    `Hi ${customerName.split(" ")[0]}, here is your ${stampedLabel} ${
      kind === "memo" ? "memo" : "invoice"
    } ${docNumber}: ${pdfUrl}`,
  );
  const waLink = waNum
    ? `https://wa.me/${waNum}?text=${waMsg}`
    : `https://wa.me/?text=${waMsg}`;

  const [copied, setCopied] = useState(false);
  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="send-panel">
      <div className="relative bg-[#0F0F0F] border border-[#7BA585]/30 w-full max-w-xl fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline text-[#7BA585]">Send to customer</div>
            <h3 className="serif text-2xl mt-1">{docNumber}</h3>
          </div>
          <button onClick={onClose} data-testid="send-panel-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          <div className="flex items-start gap-3 border border-[#7BA585]/30 bg-[#7BA585]/[0.04] p-4">
            <CheckCircle2 className="w-5 h-5 text-[#7BA585] mt-0.5 shrink-0" />
            <div className="text-sm leading-relaxed">
              Document is <strong className="text-[#7BA585]">{stampedLabel}</strong> and ready to send to{" "}
              <strong className="text-[#EAEAEA]">{customerName}</strong>
              {customerPhone && <span className="text-[#8A8A8A]"> · {customerPhone}</span>}.
              {doc.emeterai_applied && (
                <div className="mt-1 text-[0.7rem] mono text-[#7a7a7a]">
                  e-Meterai serial: <span className="text-[#C99356]">{doc.emeterai_serial}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold px-4 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
              data-testid="send-view-pdf"
            >
              <FileText className="w-3.5 h-3.5" /> View Official PDF
            </a>
            <button
              onClick={() => copy(pdfUrl)}
              className="btn-outline-gold px-4 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
              data-testid="send-copy-pdf-url"
            >
              <FileText className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy PDF URL"}
            </button>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-4 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
              data-testid="send-whatsapp"
              title={waNum ? `Open WhatsApp to ${customerPhone}` : "Open WhatsApp (no phone on file)"}
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Customer
            </a>
          </div>

          <div className="border-t border-white/5 pt-4">
            <div className="overline text-[0.55rem] text-[#7a7a7a] mb-1">Public viewer URL (no login)</div>
            <div className="mono text-xs break-all text-[#8A8A8A]">{signUrl}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact label used wherever we render a doc summary card (e.g. detail modals).
 */
export function EmeteraiBadge({ doc }) {
  if (!doc.emeterai_applied) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[0.55rem] uppercase tracking-wider text-[#C99356] border border-[#C99356]/40"
      title={`${doc.emeterai_serial} · ${(doc.emeterai_timestamp || "").slice(0, 10)}`}
      data-testid={`emeterai-badge-${doc.id || ""}`}
    >
      <Stamp className="w-2.5 h-2.5" /> e-Meterai
    </span>
  );
}
